import React, { useState } from 'react';
import {
  Vote,
  Plus,
  X,
  Check,
  Clock,
  Users,
  Calendar,
  MapPin,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  Trash2,
  Bell,
  BellOff,
  BarChart3,
  Lock,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { AddPollGate } from '../subscription/FeatureGates';

// =====================================================
// POLL NOTIFICATION BADGE - Use in Navigation/Header
// =====================================================
export const PollNotificationBadge = ({ polls = [], currentUserId }) => {
  const unvotedCount = polls.filter(poll => {
    if (poll.status !== 'active') return false;
    const hasVoted = poll.options?.some(opt => opt.votes?.includes(currentUserId));
    return !hasVoted;
  }).length;

  if (unvotedCount === 0) return null;

  return (
    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1 animate-pulse">
      {unvotedCount}
    </span>
  );
};

// =====================================================
// POLL RESULT HELPER
// =====================================================
const getPollResult = (poll) => {
  if (!poll.options) return null;
  
  const yesOption = poll.options.find(opt => opt.id === 'yes');
  const noOption = poll.options.find(opt => opt.id === 'no');
  const maybeOption = poll.options.find(opt => opt.id === 'maybe');
  
  if (!yesOption || !noOption) return null; // Not a yes/no poll
  
  const yesVotes = yesOption.votes?.length || 0;
  const noVotes = noOption.votes?.length || 0;
  const maybeVotes = maybeOption?.votes?.length || 0;
  const totalVotes = yesVotes + noVotes + maybeVotes;
  
  if (totalVotes === 0) return { result: 'no-votes', yesVotes, noVotes, maybeVotes, totalVotes };
  
  if (yesVotes > noVotes) {
    return { result: 'approved', yesVotes, noVotes, maybeVotes, totalVotes };
  } else if (noVotes > yesVotes) {
    return { result: 'rejected', yesVotes, noVotes, maybeVotes, totalVotes };
  } else {
    return { result: 'tied', yesVotes, noVotes, maybeVotes, totalVotes };
  }
};

// =====================================================
// POLL CARD COMPONENT
// =====================================================
const PollCard = ({ 
  poll, 
  currentUserId, 
  onVote, 
  onClose, 
  onDelete,
  onDeleteShow,
  onConfirmShow,
  isAdmin,
  members = []
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [showDeleteShowConfirm, setShowDeleteShowConfirm] = useState(false);
  const [showConfirmShowModal, setShowConfirmShowModal] = useState(false);

  const totalVotes = poll.options?.reduce((sum, opt) => sum + (opt.votes?.length || 0), 0) || 0;
  const hasVoted = poll.options?.some(opt => opt.votes?.includes(currentUserId));
  const userVote = poll.options?.find(opt => opt.votes?.includes(currentUserId));
  const isActive = poll.status === 'active';
  const isExpired = poll.expiresAt && new Date(poll.expiresAt) < new Date();
  const isClosed = poll.status === 'closed';
  
  // Get poll result for show offers
  const pollResult = getPollResult(poll);
  const isShowOffer = !!poll.showInfo;
  
  // Check if all members have voted
  const totalMembers = members.length || 1;
  const allVoted = totalVotes >= totalMembers;
  
  // Check if there's a clear majority (more than half of members)
  const majorityThreshold = Math.floor(totalMembers / 2) + 1;
  const yesVotes = pollResult?.yesVotes || 0;
  const noVotes = pollResult?.noVotes || 0;
  const hasClearMajority = yesVotes >= majorityThreshold || noVotes >= majorityThreshold;
  
  // Show results and action buttons if: poll is closed OR all voted OR clear majority exists
  const showResults = isClosed || allVoted || hasClearMajority;
  
  const showRejected = isShowOffer && showResults && pollResult?.result === 'rejected';
  const showApproved = isShowOffer && showResults && pollResult?.result === 'approved';

  // Get member name by ID
  const getMemberName = (userId) => {
    if (!userId) return 'Unknown';
    const member = members.find(m => m && (m.odatauserId === userId || m.odataid === userId || m.userId === userId || m.id === userId));
    return member?.name || member?.displayName || member?.email?.split('@')[0] || 'Unknown';
  };

  const handleVote = async () => {
    if (!selectedOption || hasVoted || !isActive) return;
    setIsVoting(true);
    try {
      await onVote(poll.id, selectedOption);
    } finally {
      setIsVoting(false);
    }
  };

  const getPercentage = (optionVotes) => {
    if (totalVotes === 0) return 0;
    return Math.round((optionVotes / totalVotes) * 100);
  };

  const handleDeleteShow = () => {
    if (poll.showInfo && onDeleteShow) {
      onDeleteShow(poll.showInfo, poll.id);
      setShowDeleteShowConfirm(false);
    }
  };

  const handleConfirmShow = () => {
    if (poll.showInfo && onConfirmShow) {
      onConfirmShow(poll.showInfo, poll.id);
      setShowConfirmShowModal(false);
    }
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 overflow-hidden transition-all ${
      !hasVoted && isActive 
        ? 'border-purple-400 dark:border-purple-500 ring-2 ring-purple-100 dark:ring-purple-900/30' 
        : showRejected
          ? 'border-red-300 dark:border-red-700'
          : showApproved
            ? 'border-green-300 dark:border-green-700'
            : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* Header */}
      <div 
        className={`p-4 cursor-pointer ${
          !hasVoted && isActive 
            ? 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20' 
            : showRejected
              ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20'
              : showApproved
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
                : 'bg-gray-50 dark:bg-gray-900'
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {!hasVoted && isActive && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse flex items-center gap-1">
                  <Bell size={10} />
                  Vote Needed
                </span>
              )}
              {hasVoted && isActive && !showResults && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium rounded-full flex items-center gap-1">
                  <CheckCircle2 size={10} />
                  Voted
                </span>
              )}
              {showResults && showApproved && (
                <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                  <ThumbsUp size={10} />
                  Approved
                </span>
              )}
              {showResults && showRejected && (
                <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                  <ThumbsDown size={10} />
                  Rejected
                </span>
              )}
              {isClosed && !showApproved && !showRejected && (
                <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded-full flex items-center gap-1">
                  <Lock size={10} />
                  Closed
                </span>
              )}
              {poll.showInfo && (
                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-full">
                  Show Offer
                </span>
              )}
            </div>
            
            <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">
              {poll.question}
            </h3>
            
            {poll.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {poll.description}
              </p>
            )}
          </div>
          
          <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>

        {/* Show Info if linked */}
        {poll.showInfo && (
          <div className={`mt-3 p-3 rounded-lg border ${
            showRejected 
              ? 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800' 
              : showApproved
                ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                <MapPin size={14} className="text-gray-400" />
                {poll.showInfo.venue}
              </span>
              <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                <Calendar size={14} className="text-gray-400" />
                {new Date(poll.showInfo.date).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </span>
              {poll.showInfo.guarantee && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                  <DollarSign size={14} />
                  {poll.showInfo.guarantee}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Meta info */}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1">
            <Users size={12} />
            {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {new Date(poll.createdAt).toLocaleDateString()}
          </span>
          {poll.expiresAt && (
            <span className={`flex items-center gap-1 ${isExpired ? 'text-red-500' : ''}`}>
              <AlertCircle size={12} />
              {isExpired ? 'Expired' : `Expires ${new Date(poll.expiresAt).toLocaleDateString()}`}
            </span>
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-100 dark:border-gray-700">
          {/* Result Summary for show polls when results are ready */}
          {showResults && isShowOffer && pollResult && (
            <div className={`mb-4 p-4 rounded-xl ${
              pollResult.result === 'approved' 
                ? 'bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                : pollResult.result === 'rejected'
                  ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                  : 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-center gap-3">
                {pollResult.result === 'approved' && <ThumbsUp className="text-green-600" size={24} />}
                {pollResult.result === 'rejected' && <ThumbsDown className="text-red-600" size={24} />}
                {pollResult.result === 'tied' && <AlertCircle className="text-yellow-600" size={24} />}
                <div>
                  <p className={`font-bold ${
                    pollResult.result === 'approved' ? 'text-green-800 dark:text-green-200' :
                    pollResult.result === 'rejected' ? 'text-red-800 dark:text-red-200' :
                    'text-yellow-800 dark:text-yellow-200'
                  }`}>
                    {pollResult.result === 'approved' && 'Band voted YES to this show!'}
                    {pollResult.result === 'rejected' && 'Band voted NO to this show'}
                    {pollResult.result === 'tied' && 'Vote was tied'}
                    {pollResult.result === 'no-votes' && 'No votes were cast'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Yes: {pollResult.yesVotes} • No: {pollResult.noVotes} • Maybe: {pollResult.maybeVotes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Options */}
          <div className="space-y-3">
            {poll.options?.map((option) => {
              const percentage = getPercentage(option.votes?.length || 0);
              const isSelected = selectedOption === option.id;
              const isUserVote = option.votes?.includes(currentUserId);
              
              return (
                <div key={option.id}>
                  <button
                    onClick={() => !hasVoted && isActive && setSelectedOption(option.id)}
                    disabled={hasVoted || !isActive}
                    className={`w-full relative overflow-hidden rounded-lg border-2 transition-all ${
                      hasVoted || !isActive
                        ? isUserVote
                          ? 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                        : isSelected
                          ? 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
                    }`}
                  >
                    {/* Progress bar background */}
                    {(hasVoted || !isActive) && (
                      <div 
                        className={`absolute inset-y-0 left-0 transition-all ${
                          isUserVote 
                            ? 'bg-purple-200 dark:bg-purple-800/40' 
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    )}
                    
                    <div className="relative flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        {/* Radio/Check indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          hasVoted || !isActive
                            ? isUserVote
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300 dark:border-gray-600'
                            : isSelected
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {(isUserVote || isSelected) && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>
                        
                        <span className={`font-medium ${
                          isUserVote ? 'text-purple-700 dark:text-purple-300' : 'text-gray-700 dark:text-gray-200'
                        }`}>
                          {option.text}
                        </span>
                      </div>
                      
                      {(hasVoted || !isActive) && (
                        <span className={`font-bold ${
                          isUserVote ? 'text-purple-700 dark:text-purple-300' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {percentage}%
                        </span>
                      )}
                    </div>
                  </button>
                  
                  {/* Show who voted (after voting) */}
                  {(hasVoted || !isActive) && option.votes?.length > 0 && (
                    <div className="mt-1 ml-8 text-xs text-gray-500 dark:text-gray-400">
                      {option.votes.map(uid => getMemberName(uid)).join(', ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Vote Button */}
          {!hasVoted && isActive && (
            <button
              onClick={handleVote}
              disabled={!selectedOption || isVoting}
              className={`w-full mt-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                selectedOption
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {isVoting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Vote size={18} />
                  Submit Vote
                </>
              )}
            </button>
          )}

          {/* Show Action Buttons when results are ready */}
          {isAdmin && showResults && isShowOffer && (
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              {showRejected && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    The band voted against this show. Would you like to remove it?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteShowConfirm(true)}
                      className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 size={16} />
                      Delete Show
                    </button>
                    <button
                      onClick={() => onDelete(poll.id)}
                      className="py-2.5 px-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Keep It
                    </button>
                  </div>
                </div>
              )}
              
              {showApproved && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    The band approved this show! Would you like to confirm it?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowConfirmShowModal(true)}
                      className="flex-1 py-2.5 px-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 size={16} />
                      Confirm Show
                    </button>
                    <button
                      onClick={() => onDelete(poll.id)}
                      className="py-2.5 px-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Admin Actions */}
          {isAdmin && (
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              {isActive && (
                <button
                  onClick={() => onClose(poll.id)}
                  className="flex-1 py-2 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                >
                  <Lock size={16} />
                  Close Poll
                </button>
              )}
              <button
                onClick={() => onDelete(poll.id)}
                className="py-2 px-4 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* Delete Show Confirmation Modal */}
      {showDeleteShowConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteShowConfirm(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Delete This Show?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                This will remove the tentative show from your calendar:
              </p>
              <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-3 mb-4">
                <p className="font-semibold text-gray-800 dark:text-gray-100">{poll.showInfo?.venue}</p>
                <p className="text-sm text-gray-500">{poll.showInfo?.city} • {new Date(poll.showInfo?.date).toLocaleDateString()}</p>
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mb-6">
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteShowConfirm(false)}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteShow}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  Delete Show
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Show Modal */}
      {showConfirmShowModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConfirmShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">Confirm This Show?</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                This will change the show status from tentative to confirmed:
              </p>
              <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-3 mb-6">
                <p className="font-semibold text-gray-800 dark:text-gray-100">{poll.showInfo?.venue}</p>
                <p className="text-sm text-gray-500">{poll.showInfo?.city} • {new Date(poll.showInfo?.date).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmShowModal(false)}
                  className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmShow}
                  className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle2 size={18} />
                  Confirm Show
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// CREATE POLL MODAL
// =====================================================
const CreatePollModal = ({ 
  isOpen, 
  onClose, 
  onCreate,
  upcomingShows = []
}) => {
  const [pollType, setPollType] = useState('yesno'); // 'yesno' | 'custom' | 'show'
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [customOptions, setCustomOptions] = useState(['', '']);
  const [selectedShow, setSelectedShow] = useState(null);
  const [expiresIn, setExpiresIn] = useState(''); // days
  const [isCreating, setIsCreating] = useState(false);

  if (!isOpen) return null;

  const handleAddOption = () => {
    if (customOptions.length < 6) {
      setCustomOptions([...customOptions, '']);
    }
  };

  const handleRemoveOption = (index) => {
    if (customOptions.length > 2) {
      setCustomOptions(customOptions.filter((_, i) => i !== index));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...customOptions];
    newOptions[index] = value;
    setCustomOptions(newOptions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreating(true);

    let options;
    if (pollType === 'yesno' || pollType === 'show') {
      options = [
        { id: 'yes', text: "Yes, I'm in! ✅", votes: [] },
        { id: 'no', text: "No, can't do it ❌", votes: [] },
        { id: 'maybe', text: "Maybe, need more info 🤔", votes: [] },
      ];
    } else {
      options = customOptions
        .filter(opt => opt.trim())
        .map((text, i) => ({ id: `opt-${i}`, text, votes: [] }));
    }

    const pollData = {
      id: Date.now(),
      question: pollType === 'show' && selectedShow 
        ? `Should we take this show: ${selectedShow.venue}?`
        : question,
      description,
      options,
      status: 'active',
      createdAt: new Date().toISOString(),
      expiresAt: expiresIn ? new Date(Date.now() + parseInt(expiresIn) * 24 * 60 * 60 * 1000).toISOString() : null,
      showInfo: pollType === 'show' && selectedShow ? {
        showId: selectedShow.id,
        dateKey: selectedShow.dateKey,
        venue: selectedShow.venue,
        city: selectedShow.city,
        date: selectedShow.date,
        guarantee: selectedShow.guarantee,
      } : null,
    };

    try {
      await onCreate(pollData);
      // Reset form
      setQuestion('');
      setDescription('');
      setCustomOptions(['', '']);
      setSelectedShow(null);
      setExpiresIn('');
      setPollType('yesno');
      onClose();
    } finally {
      setIsCreating(false);
    }
  };

  // Validate that show polls have venue and city
  const isShowValid = selectedShow && 
    selectedShow.venue?.trim() && 
    selectedShow.city?.trim();

  const isValid = pollType === 'show' 
    ? isShowValid 
    : pollType === 'custom' 
      ? customOptions.filter(o => o.trim()).length >= 2 && question.trim()
      : question.trim();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-xl">
              <Vote className="text-purple-600 dark:text-purple-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Create Poll</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Get input from your band</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Poll Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Poll Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'yesno', label: 'Yes/No', icon: '✅' },
                  { id: 'show', label: 'Show Offer', icon: '🎤' },
                  { id: 'custom', label: 'Custom', icon: '📝' },
                ].map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setPollType(type.id)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      pollType === type.id
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className={`text-sm font-medium ${
                      pollType === type.id 
                        ? 'text-purple-700 dark:text-purple-300' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {type.label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Show Selection (for show offer polls) */}
            {pollType === 'show' && upcomingShows.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Show Offer
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {upcomingShows.map((show) => {
                    // Check if venue and city are filled out
                    const hasVenue = show.venue && show.venue.trim() !== '';
                    const hasCity = show.city && show.city.trim() !== '';
                    const isComplete = hasVenue && hasCity;
                    
                    return (
                      <button
                        key={show.id}
                        type="button"
                        onClick={() => {
                          if (!isComplete) {
                            // Can't select incomplete show
                            return;
                          }
                          setSelectedShow(show);
                        }}
                        disabled={!isComplete}
                        className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                          !isComplete
                            ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 opacity-60 cursor-not-allowed'
                            : selectedShow?.id === show.id
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="font-medium text-gray-800 dark:text-gray-100">
                              {hasVenue ? show.venue : <span className="text-red-500 italic">Missing venue</span>}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {hasCity ? show.city : <span className="text-red-500 italic">Missing city</span>}
                              {show.date && ` • ${new Date(show.date).toLocaleDateString()}`}
                              {show.guarantee && ` • $${show.guarantee}`}
                            </div>
                          </div>
                          {!isComplete && (
                            <div className="flex-shrink-0">
                              <AlertCircle size={18} className="text-red-500" />
                            </div>
                          )}
                        </div>
                        {!isComplete && (
                          <div className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                            <AlertCircle size={12} />
                            Venue and city required to create poll
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {/* Warning if all shows are incomplete */}
                {upcomingShows.length > 0 && upcomingShows.every(show => !show.venue?.trim() || !show.city?.trim()) && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <AlertCircle size={16} />
                      All tentative shows are missing venue or city. Please update show details in Calendar first.
                    </p>
                  </div>
                )}
              </div>
            )}

            {pollType === 'show' && upcomingShows.length === 0 && (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  No upcoming shows with "tentative" status. Add a tentative show first, or use a Yes/No poll.
                </p>
              </div>
            )}

            {/* Question */}
            {pollType !== 'show' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="e.g., Should we book this venue?"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Details (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add any context or details..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Custom Options */}
            {pollType === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Options
                </label>
                <div className="space-y-2">
                  {customOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      {customOptions.length > 2 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {customOptions.length < 6 && (
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="mt-2 text-purple-600 dark:text-purple-400 text-sm font-medium hover:underline flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Option
                  </button>
                )}
              </div>
            )}

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Auto-close after (optional)
              </label>
              <select
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Never (manual close)</option>
                <option value="1">1 day</option>
                <option value="3">3 days</option>
                <option value="7">1 week</option>
                <option value="14">2 weeks</option>
              </select>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || isCreating}
              className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 ${
                isValid && !isCreating
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {isCreating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Vote size={18} />
                  Create Poll
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =====================================================
// MAIN POLLS COMPONENT
// =====================================================
const Polls = ({
  polls = [],
  currentUserId,
  isAdmin = false,
  members = [],
  upcomingShows = [],
  onAddPoll,
  onVote,
  onClosePoll,
  onDeletePoll,
  onDeleteShow,
  onConfirmShow,
  onNavigate,
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('active'); // 'all' | 'active' | 'voted' | 'unvoted'

  // Calculate unvoted count for notification
  const unvotedPolls = polls.filter(poll => {
    if (poll.status !== 'active') return false;
    const hasVoted = poll.options?.some(opt => opt.votes?.includes(currentUserId));
    return !hasVoted;
  });

  // Filter polls
  const filteredPolls = polls.filter(poll => {
    const hasVoted = poll.options?.some(opt => opt.votes?.includes(currentUserId));
    switch (filter) {
      case 'active': return poll.status === 'active';
      case 'voted': return hasVoted;
      case 'unvoted': return poll.status === 'active' && !hasVoted;
      default: return true;
    }
  });

  // Sort: unvoted first, then by date
  const sortedPolls = [...filteredPolls].sort((a, b) => {
    const aVoted = a.options?.some(opt => opt.votes?.includes(currentUserId));
    const bVoted = b.options?.some(opt => opt.votes?.includes(currentUserId));
    if (aVoted !== bVoted) return aVoted ? 1 : -1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 md:pb-8">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3">
          <button 
            onClick={() => onNavigate('dashboard')} 
            className="p-2 rounded-full bg-gray-100 dark:bg-gray-700"
          >
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            Polls
            {unvotedPolls.length > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                {unvotedPolls.length}
              </span>
            )}
          </h1>
          {isAdmin ? (
            <AddPollGate currentCount={polls.filter(p => p.status === 'active').length}>
              <button
                onClick={() => setShowCreateModal(true)}
                className="p-2 rounded-full bg-purple-600 text-white"
              >
                <Plus size={20} />
              </button>
            </AddPollGate>
          ) : (
            <div className="w-10" /> // Spacer for alignment
          )}
        </div>

        {/* Desktop Header */}
        <div className="hidden md:block max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <BarChart3 className="text-purple-600" />
                Polls
                {unvotedPolls.length > 0 && (
                  <span className="px-2.5 py-1 bg-red-500 text-white text-sm font-bold rounded-full">
                    {unvotedPolls.length} new
                  </span>
                )}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Vote on band decisions together</p>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <AddPollGate currentCount={polls.filter(p => p.status === 'active').length}>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
                  >
                    <Plus size={20} />
                    Create Poll
                  </button>
                </AddPollGate>
              )}
              <button
                onClick={() => onNavigate('dashboard')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Unvoted Alert */}
        {unvotedPolls.length > 0 && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Bell className="text-white" size={20} />
              </div>
              <div>
                <p className="font-semibold text-purple-800 dark:text-purple-200">
                  You have {unvotedPolls.length} poll{unvotedPolls.length !== 1 ? 's' : ''} waiting for your vote!
                </p>
                <p className="text-sm text-purple-600 dark:text-purple-400">
                  Your input helps the band make decisions together.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'active', label: 'Active' },
            { id: 'unvoted', label: 'Need Vote' },
            { id: 'voted', label: 'Voted' },
            { id: 'all', label: 'All' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                filter === f.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Polls List */}
        {sortedPolls.length > 0 ? (
          <div className="space-y-4">
            {sortedPolls.map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                currentUserId={currentUserId}
                onVote={onVote}
                onClose={onClosePoll}
                onDelete={onDeletePoll}
                onDeleteShow={onDeleteShow}
                onConfirmShow={onConfirmShow}
                isAdmin={isAdmin}
                members={members}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Vote size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
              No polls found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {filter === 'unvoted' 
                ? "You're all caught up! No votes needed."
                : isAdmin 
                  ? "Create a poll to get input from your band."
                  : "No polls have been created yet."}
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreatePollModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={onAddPoll}
        upcomingShows={upcomingShows.filter(s => s.status === 'tentative')}
      />
    </div>
  );
};

export default Polls;